/**
 * Remote v1 — QR + MQTT `lectura/v1/...` + server-backed session ids.
 * @see docs/REMOTE-SESSION-ARCHITECTURE-V1.md
 */

import { resolveDeckBasePath, resolveRemoteOrigin } from '../services/remote-session';
import {
  buildRemoteV1SessionEndedState,
  buildRemoteV1StateFromReveal,
  createRemoteMqttPresenterV1Service,
  getRevealDeckSnapshot,
  isRevealOnLastSlide,
  RemoteSessionHttpError,
  type CreatePresenterSessionResultV1,
  type RemoteMqttPresenterV1Service,
  type RemoteSessionApiV1,
  type RemoteV1EmotePayload
} from '../services/remote/v1';
import { showRemoteEmoteToast } from './remote/emote-toast';
import { handlePresenterRaiseHandEmote } from './remote/raise-hand-queue';
import {
  ensureQrOverlay,
  OPEN_ICON,
  OPEN_ICON_VIEWBOX,
  QR_ICON,
  QR_ICON_VIEWBOX,
  qrOverlayDebug,
  renderQrOverlayContent,
  renderQrOverlayLoading,
  resolvePresentationIdForRemote,
  setQrOverlayMqttError
} from './remote/qr-overlay';
import type { RemoteApi, RemoteSlots } from './remote-types';
import { EVENTS, on } from '../core/events';

export interface RegisterRemoteV1Opts extends RemoteSlots {
  sessionApi: RemoteSessionApiV1;
  /** When API omits mqtt URL (should not happen), use chat MQTT or HiveMQ default. */
  mqttUrlFallback: string;
  /**
   * When set, POST /sessions is skipped until this resolves to a non-empty token.
   * Avoids 401 while Firebase auth is still restoring (e.g. first `onAuthStateChanged` before anonymous sign-in).
   */
  getRemoteSessionBearer?: () => Promise<string | null>;
  /** Firebase uid (or other stable key) — used to reset session-create failure state when the signed-in user changes. */
  getRemoteSessionUserKey?: () => string | null;
}

type EnsurePresenterReadyResult = { ok: true } | { ok: false; message: string };

function isPermissionDeniedRemoteSession(err: unknown): boolean {
  return (
    err instanceof RemoteSessionHttpError && err.status === 403 && err.code === 'permission-denied'
  );
}

function buildRemotePageUrl(remotePath: string): string {
  const path = remotePath.startsWith('/') ? remotePath : `/${remotePath}`;
  const origin = resolveRemoteOrigin();
  return `${origin}${path}`;
}

/** Matches default buttons on `remote/index.html` when payload has no `label`. */
const DEFAULT_REMOTE_EMOTE_LABELS: Partial<Record<string, string>> = {
  '👍': 'Thumbs up',
  '👏': 'Applause',
  '❤️': 'Love',
  '😂': 'Funny',
  '🤔': 'Thinking',
  '🔥': 'Fire',
  '⏬': 'Slow down',
  '⏫': 'Speed up',
  '❓': 'Confused',
  '\u270B': 'Raise hand'
};

export function registerRemoteV1(opts: RegisterRemoteV1Opts): RemoteApi {
  const remoteSlot = opts.remoteSlot ?? document.querySelector<HTMLElement>('.lectura-bar-remote');
  if (!remoteSlot) {
    qrOverlayDebug('Remote slot not found; remote v1 disabled');
    return { toggleQR: () => {}, isQRVisible: () => false };
  }

  const revealEl = document.querySelector<HTMLElement>('.reveal') ?? document.body;
  const overlay = ensureQrOverlay(revealEl);
  const presId = () => resolvePresentationIdForRemote(opts.getPresentationId);
  const mqttFallback = String(opts.mqttUrlFallback || '').trim() || 'wss://broker.hivemq.com:8884/mqtt';

  let activeSession: CreatePresenterSessionResultV1 | null = null;
  let stateSeq = 0;
  let presenterService: RemoteMqttPresenterV1Service | null = null;
  let startedPublishing = false;
  let revealSlideListenersAttached = false;
  let persistStateTimer: ReturnType<typeof setTimeout> | null = null;
  const PERSIST_STATE_MS = 550;

  let unloadSessionEnded = false;
  let recyclingSession = false;
  let recycleFailureCount = 0;

  let warmDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  let warmInFlight = false;
  let unsubUserChanged: (() => void) | null = null;
  /** After a 403 permission-denied on POST /sessions, skip further creates until auth changes. */
  let sessionCreatePermissionDenied = false;
  let sessionPermissionDeniedMessage = '';
  let lastRemoteSessionUserKey: string | null = null;
  /** Coalesces concurrent `ensurePresenterRemoteReady` calls so only one POST /sessions runs until `activeSession` is set. */
  let ensurePresenterSessionPromise: Promise<EnsurePresenterReadyResult> | null = null;

  function scheduleHttpStatePersist(): void {
    const api = opts.sessionApi;
    if (typeof api.patchSessionState !== 'function') return;
    if (persistStateTimer) clearTimeout(persistStateTimer);
    persistStateTimer = setTimeout(() => {
      persistStateTimer = null;
      const sid = activeSession?.session.id;
      const st = getStatePayload();
      if (!sid || !st) return;
      void api.patchSessionState(sid, st).catch((err) => {
        qrOverlayDebug('remote-v1-patch-state-failed', { error: String(err) });
      });
    }, PERSIST_STATE_MS);
  }

  function flushHttpStatePersist(): void {
    if (persistStateTimer) {
      clearTimeout(persistStateTimer);
      persistStateTimer = null;
    }
    const api = opts.sessionApi;
    const sid = activeSession?.session.id;
    const st = getStatePayload();
    if (!sid || !st || typeof api.patchSessionState !== 'function') return;
    void api.patchSessionState(sid, st).catch((err) => {
      qrOverlayDebug('remote-v1-patch-state-failed', { error: String(err) });
    });
  }

  function isQrOverlayOpen(): boolean {
    return (
      overlay.classList.contains('rf-qr-overlay-open') || overlay.classList.contains('lectura-qr-visible')
    );
  }

  function getStatePayload() {
    const snap = getRevealDeckSnapshot(opts.slidesVersion || '');
    if (!snap) return null;
    stateSeq += 1;
    return buildRemoteV1StateFromReveal({
      version: stateSeq,
      indices: snap.indices,
      totalSlides: snap.totalSlides,
      title: snap.title,
      notesPlain: snap.notesPlain,
      slidesVersion: opts.slidesVersion || '',
      highlightColorRgb: snap.highlightColorRgb,
      ttsLang: snap.ttsLang,
      notesSourceLang: snap.notesSourceLang
    });
  }

  function firePresenterUnloadEnd(): void {
    if (unloadSessionEnded) return;
    unloadSessionEnded = true;
    flushHttpStatePersist();
    const sid = activeSession?.session.id;
    try {
      const ps = presenterService;
      if (ps && sid) {
        const last = getStatePayload();
        ps.publishStatePayload(buildRemoteV1SessionEndedState(last));
      }
    } catch {
      /* ignore */
    }
    presenterService?.stop();
    presenterService = null;
    if (sid) {
      void opts.sessionApi.endPresenterSession(sid, { keepalive: true }).catch(() => {});
    }
    activeSession = null;
    startedPublishing = false;
    stateSeq = 0;
    if (persistStateTimer) {
      clearTimeout(persistStateTimer);
      persistStateTimer = null;
    }
    unsubUserChanged?.();
    unsubUserChanged = null;
    if (warmDebounceTimer) {
      clearTimeout(warmDebounceTimer);
      warmDebounceTimer = null;
    }
  }

  function scheduleBackgroundWarmup(): void {
    if (unloadSessionEnded || recyclingSession) return;
    if (warmDebounceTimer) clearTimeout(warmDebounceTimer);
    warmDebounceTimer = setTimeout(() => {
      warmDebounceTimer = null;
      void warmRemoteSessionInBackground();
    }, 450);
  }

  async function hasRemoteSessionCredential(): Promise<boolean> {
    if (!opts.getRemoteSessionBearer) return true;
    try {
      const t = await opts.getRemoteSessionBearer();
      const ok = !!t?.trim();
      if (!ok) {
        qrOverlayDebug('remote-v1-skip-session-create-no-token', { hasGetter: true });
      }
      return ok;
    } catch {
      return false;
    }
  }

  function recordPermissionDenied(err: unknown): void {
    sessionCreatePermissionDenied = true;
    sessionPermissionDeniedMessage =
      err instanceof RemoteSessionHttpError ? err.message : String(err);
  }

  function startPresenterMqttAfterSessionReady(): EnsurePresenterReadyResult {
    if (!startedPublishing) startedPublishing = true;
    const ps = ensurePresenterService();
    void ps?.start().catch((e: unknown) => {
      qrOverlayDebug('remote-v1-mqtt-start-failed', { error: String(e) });
      if (isQrOverlayOpen()) {
        window.setTimeout(() => void recycleRemoteSession(), 150);
      } else {
        presenterService?.stop();
        presenterService = null;
        startedPublishing = false;
        scheduleBackgroundWarmup();
      }
    });
    ps?.publishState();
    return { ok: true };
  }

  /**
   * Create server session (if needed), build presenter MQTT client, call start() + publishState.
   * Used by background warmup and when opening the QR overlay.
   */
  async function ensurePresenterRemoteReady(): Promise<EnsurePresenterReadyResult> {
    if (unloadSessionEnded || recyclingSession) {
      return { ok: false, message: 'Remote is not available right now.' };
    }
    if (sessionCreatePermissionDenied) {
      return {
        ok: false,
        message:
          sessionPermissionDeniedMessage ||
          'You do not have permission to start a remote session for this deck.'
      };
    }
    if (activeSession) {
      return startPresenterMqttAfterSessionReady();
    }
    if (!(await hasRemoteSessionCredential())) {
      return { ok: false, message: 'Waiting for sign-in before remote can start…' };
    }
    if (ensurePresenterSessionPromise) {
      return ensurePresenterSessionPromise;
    }
    ensurePresenterSessionPromise = (async (): Promise<EnsurePresenterReadyResult> => {
      try {
        if (activeSession) {
          return startPresenterMqttAfterSessionReady();
        }
        try {
          const base = resolveDeckBasePath();
          activeSession = await opts.sessionApi.createPresenterSession(presId(), {
            embedBasePath: base === '/' ? undefined : base,
            debugCaller: 'register-remote-v1:ensurePresenterRemoteReady'
          });
          stateSeq = 0;
          qrOverlayDebug('remote-v1-session-created', { sessionId: activeSession.session.id });
        } catch (err) {
          qrOverlayDebug('remote-v1-create-session-failed', { error: String(err) });
          if (isPermissionDeniedRemoteSession(err)) {
            recordPermissionDenied(err);
          }
          return {
            ok: false,
            message: err instanceof Error ? err.message : String(err)
          };
        }
        return startPresenterMqttAfterSessionReady();
      } finally {
        ensurePresenterSessionPromise = null;
      }
    })();
    return ensurePresenterSessionPromise;
  }

  async function warmRemoteSessionInBackground(): Promise<void> {
    if (unloadSessionEnded || recyclingSession || warmInFlight) return;
    if (activeSession && presenterService) return;
    warmInFlight = true;
    try {
      await ensurePresenterRemoteReady();
    } finally {
      warmInFlight = false;
    }
  }

  async function recycleRemoteSession(): Promise<void> {
    if (recyclingSession) return;
    recyclingSession = true;
    setQrOverlayMqttError(overlay, false);
    renderQrOverlayLoading(overlay);
    try {
      const oldSid = activeSession?.session.id;
      presenterService?.stop();
      presenterService = null;
      if (oldSid) {
        void opts.sessionApi.endPresenterSession(oldSid).catch(() => {});
      }
      activeSession = null;
      startedPublishing = false;
      stateSeq = 0;
      if (persistStateTimer) {
        clearTimeout(persistStateTimer);
        persistStateTimer = null;
      }
      if (!(await hasRemoteSessionCredential())) {
        qrOverlayDebug('remote-v1-recycle-wait-token');
        window.setTimeout(() => void recycleRemoteSession(), 500);
        return;
      }
      const base = resolveDeckBasePath();
      activeSession = await opts.sessionApi.createPresenterSession(presId(), {
        embedBasePath: base === '/' ? undefined : base,
        debugCaller: 'register-remote-v1:recycleRemoteSession'
      });
      stateSeq = 0;
      const remoteUrl = buildRemotePageUrl(activeSession.remote_path);
      renderQrOverlayContent(overlay, remoteUrl);
      startedPublishing = true;
      const ps = ensurePresenterService();
      await ps?.start().catch((e: unknown) => {
        qrOverlayDebug('remote-v1-recycle-start-failed', { error: String(e) });
        window.setTimeout(() => void recycleRemoteSession(), 150);
      });
      ps?.publishState();
      recycleFailureCount = 0;
    } catch (err) {
      qrOverlayDebug('remote-v1-recycle-failed', { error: String(err) });
      if (isPermissionDeniedRemoteSession(err)) {
        recordPermissionDenied(err);
        qrOverlayDebug('remote-v1-recycle-permission-denied', {
          message: err instanceof RemoteSessionHttpError ? err.message : String(err)
        });
        closeQrOverlayUi();
        recycleFailureCount = 0;
        return;
      }
      recycleFailureCount += 1;
      if (recycleFailureCount < 8) {
        window.setTimeout(() => void recycleRemoteSession(), 2500);
      } else {
        renderQrOverlayLoading(overlay);
      }
    } finally {
      recyclingSession = false;
    }
  }

  function ensurePresenterService(): RemoteMqttPresenterV1Service | null {
    if (presenterService || !activeSession) return presenterService;
    const mqttUrl = activeSession.mqtt_url || mqttFallback;
    presenterService = createRemoteMqttPresenterV1Service(
      mqttUrl,
      {
        presentationId: activeSession.presentation_id,
        sessionId: activeSession.session.id,
        participantId: activeSession.participant_id,
        commandToken: activeSession.command_token,
        mqttAuth: null
      },
      {
        getState: getStatePayload,
        onCommandApplied: () => presenterService?.publishState(),
        onStatePublished: () => scheduleHttpStatePersist(),
        onEmote: (em: RemoteV1EmotePayload, meta: { participantId?: string }) => {
          if (handlePresenterRaiseHandEmote(em, meta.participantId)) return;
          const label =
            (typeof em.label === 'string' && em.label.trim()) ||
            DEFAULT_REMOTE_EMOTE_LABELS[String(em.emoji || '').trim()];
          showRemoteEmoteToast(em.emoji, {
            label,
            durationMs: em.ttl_ms,
            scale: em.scale,
            layout: isRevealOnLastSlide() ? 'last_slide' : 'default'
          });
        },
        onStatus: (status) => {
          qrOverlayDebug('remote-v1-presenter-status', { status });
          if (status === 'connected' || status === 'connecting') {
            setQrOverlayMqttError(overlay, false);
          }
          if (status === 'error') {
            if (isQrOverlayOpen()) {
              void recycleRemoteSession();
            } else {
              presenterService?.stop();
              presenterService = null;
              startedPublishing = false;
              scheduleBackgroundWarmup();
            }
          }
        },
        onDebug: (message, data) => qrOverlayDebug(`remote-v1:${message}`, data || {})
      }
    );
    if (!revealSlideListenersAttached && typeof Reveal !== 'undefined' && typeof Reveal.on === 'function') {
      revealSlideListenersAttached = true;
      const slidePublishHandler = (): void => {
        presenterService?.publishState();
      };
      Reveal.on('slidechanged', slidePublishHandler);
      Reveal.on('fragmentshown', slidePublishHandler);
      Reveal.on('fragmenthidden', slidePublishHandler);
    }
    return presenterService;
  }

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'lectura-bar-btn lectura-bar-qr-btn';
  btn.setAttribute('aria-label', 'QR Remote');
  btn.title = 'Remote QR (S)';
  btn.innerHTML = `<svg class="lectura-bar-btn-icon lectura-bar-qr-icon" viewBox="${QR_ICON_VIEWBOX}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${QR_ICON}</svg>`;
  remoteSlot.appendChild(btn);

  const qrIconSvg = btn.querySelector<SVGElement>('.lectura-bar-qr-icon');

  function applyButtonState(open: boolean): void {
    btn.classList.toggle('lectura-bar-btn-active', open);
    if (!qrIconSvg) return;
    qrIconSvg.setAttribute('viewBox', open ? OPEN_ICON_VIEWBOX : QR_ICON_VIEWBOX);
    qrIconSvg.innerHTML = open ? OPEN_ICON : QR_ICON;
  }

  /** Close QR overlay without showing an error banner — failures are logged only (see qrOverlayDebug). */
  function closeQrOverlayUi(): void {
    overlay.innerHTML = '';
    overlay.classList.remove('rf-qr-overlay-open', 'lectura-qr-visible');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('lectura-qr-overlay-visible');
    applyButtonState(false);
  }

  async function setOpen(open: boolean): Promise<void> {
    if (open) {
      setQrOverlayMqttError(overlay, false);
      const needWait = !activeSession || !presenterService;
      if (needWait) {
        renderQrOverlayLoading(overlay);
      }
      const ready = await ensurePresenterRemoteReady();
      if (!ready.ok || !activeSession) {
        const message = ready.ok ? 'Could not start remote session.' : ready.message;
        qrOverlayDebug('remote-v1-open-qr-failed', { message });
        closeQrOverlayUi();
        return;
      }
      const remoteUrl = buildRemotePageUrl(activeSession.remote_path);
      renderQrOverlayContent(overlay, remoteUrl);
      qrOverlayDebug('remote-v1-opening-qr', { remoteUrl });
      presenterService?.publishState();
    } else {
      qrOverlayDebug('remote-v1-closing-qr');
      flushHttpStatePersist();
      setQrOverlayMqttError(overlay, false);
    }
    overlay.classList.toggle('rf-qr-overlay-open', open);
    overlay.classList.toggle('lectura-qr-visible', open);
    overlay.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.body.classList.toggle('lectura-qr-overlay-visible', open);
    applyButtonState(open);
  }

  function isQRVisible(): boolean {
    return overlay.classList.contains('rf-qr-overlay-open');
  }

  function toggleQR(): void {
    qrOverlayDebug('remote-v1-qr-click', { open: !isQRVisible() });
    void setOpen(!isQRVisible());
  }

  btn.addEventListener('click', toggleQR);
  overlay.addEventListener('click', (e: MouseEvent) => {
    if (e.target === overlay) void setOpen(false);
  });

  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isQRVisible()) void setOpen(false);
  });

  window.addEventListener('pagehide', (ev: PageTransitionEvent) => {
    if (ev.persisted) return;
    firePresenterUnloadEnd();
  });
  window.addEventListener('beforeunload', () => {
    firePresenterUnloadEnd();
  });

  unsubUserChanged = on(EVENTS.USER_CHANGED, () => {
    const key = opts.getRemoteSessionUserKey?.() ?? null;
    if (key !== lastRemoteSessionUserKey) {
      lastRemoteSessionUserKey = key;
      sessionCreatePermissionDenied = false;
      sessionPermissionDeniedMessage = '';
    }
  });

  return { toggleQR, isQRVisible, scheduleRemoteWarmup: scheduleBackgroundWarmup };
}
